// The expression grammar (E01-S2-T1; contracts 1.0 section 2): declarative, no loops, no
// functions, no string building. Exactly the ten operators; each node has exactly one.
import { z } from "zod";
import { Id, strictObject } from "../primitives.js";
import { parseRef } from "./refs.js";
export const EXPR_OPERATORS = [
    "all",
    "any",
    "not",
    "screen",
    "overlay",
    "signal",
    "intent",
    "field",
    "gt",
    "eq",
];
export const EXPR_MAX_DEPTH = 16;
export const EXPR_MAX_NODES = 256;
const RefSchema = z
    .string()
    .refine((s) => s.startsWith("$") && parseRef(s) !== null, "not an allow-listed reference");
/** The zod shape of one node. Recursive; bounded by parseExpr before this ever runs. */
export const ExprSchema = z.lazy(() => z.union([
    strictObject({ all: z.array(ExprSchema) }),
    strictObject({ any: z.array(ExprSchema) }),
    strictObject({ not: ExprSchema }),
    strictObject({ screen: Id }),
    strictObject({ overlay: Id }),
    strictObject({ signal: Id }),
    strictObject({ intent: Id }),
    strictObject({
        field: Id,
        present: z.boolean().optional(),
        equals: z.string().optional(),
        in: z.array(z.string()).optional(),
    }).refine((f) => [f.equals, f.in].filter((x) => x !== undefined).length <= 1, {
        message: "equals and in are exclusive",
    }),
    strictObject({ gt: z.tuple([RefSchema, z.union([RefSchema, z.number()])]) }),
    strictObject({ eq: z.tuple([RefSchema, z.union([z.number(), z.string()])]) }),
]));
const isPlain = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
const OPS = new Set(EXPR_OPERATORS);
/**
 * parseExpr never throws. It walks the tree iteratively first — operator shape, depth ≤ 16,
 * nodes ≤ 256 — so a 10,000-level input is refused before any recursion, then hands a bounded
 * tree to the zod schema for operand checks.
 */
export function parseExpr(input) {
    try {
        return parse(input);
    }
    catch {
        return {
            ok: false,
            errors: [{ code: "expr.not-object", path: "", message: "the input could not be read" }],
        };
    }
}
function parse(input) {
    const errors = [];
    const stack = [
        { node: input, path: "", depth: 1 },
    ];
    let nodes = 0;
    for (let item = stack.pop(); item !== undefined; item = stack.pop()) {
        const { node, path, depth } = item;
        nodes += 1;
        if (nodes > EXPR_MAX_NODES) {
            errors.push({
                code: "expr.too-large",
                path,
                message: `more than ${String(EXPR_MAX_NODES)} nodes`,
            });
            break;
        }
        if (depth > EXPR_MAX_DEPTH) {
            errors.push({
                code: "expr.too-deep",
                path,
                message: `deeper than ${String(EXPR_MAX_DEPTH)}`,
            });
            break;
        }
        if (!isPlain(node)) {
            errors.push({ code: "expr.not-object", path, message: "an expression node is an object" });
            continue;
        }
        const keys = Object.keys(node);
        const ops = keys.filter((k) => OPS.has(k));
        const op = ops[0];
        if (op === undefined) {
            errors.push({
                code: "expr.unknown-operator",
                path,
                message: `no operator among ${keys.join(", ") || "(none)"}`,
            });
            continue;
        }
        if (ops.length > 1) {
            errors.push({
                code: "expr.multiple-operators",
                path,
                message: `one operator per node, found ${ops.join(", ")}`,
            });
            continue;
        }
        const extra = keys.filter((k) => !OPS.has(k) && !(op === "field" && (k === "present" || k === "equals" || k === "in")));
        if (extra.length)
            errors.push({
                code: "expr.unknown-operator",
                path,
                message: `unknown key ${extra.join(", ")}`,
            });
        const v = node[op];
        if (op === "all" || op === "any") {
            if (!Array.isArray(v)) {
                errors.push({
                    code: "expr.invalid-operand",
                    path: `${path}/${op}`,
                    message: `${op} takes a list`,
                });
                continue;
            }
            for (let i = v.length - 1; i >= 0; i -= 1)
                stack.push({ node: v[i], path: `${path}/${op}/${String(i)}`, depth: depth + 1 });
        }
        else if (op === "not") {
            stack.push({ node: v, path: `${path}/not`, depth: depth + 1 });
        }
        else if (op === "gt" || op === "eq") {
            if (!Array.isArray(v) || v.length !== 2) {
                errors.push({
                    code: "expr.invalid-operand",
                    path: `${path}/${op}`,
                    message: `${op} takes [ref, operand]`,
                });
                continue;
            }
            const check = (x, i, allowString) => {
                if (typeof x === "string" && x.startsWith("$")) {
                    if (!parseRef(x))
                        errors.push({
                            code: "expr.invalid-ref",
                            path: `${path}/${op}/${String(i)}`,
                            message: `${x} is not an allow-listed reference`,
                        });
                }
                else if (i === 0)
                    errors.push({
                        code: "expr.invalid-ref",
                        path: `${path}/${op}/0`,
                        message: "the first operand is a reference",
                    });
                else if (typeof x === "number") {
                    if (!Number.isFinite(x))
                        errors.push({
                            code: "expr.invalid-operand",
                            path: `${path}/${op}/1`,
                            message: "not a finite number",
                        });
                }
                else if (!(allowString && typeof x === "string"))
                    errors.push({
                        code: "expr.invalid-operand",
                        path: `${path}/${op}/1`,
                        message: `${op}'s operand is a reference${allowString ? ", a number or a string" : " or a number"}`,
                    });
            };
            check(v[0], 0, false);
            check(v[1], 1, op === "eq");
        }
    }
    if (errors.length)
        return { ok: false, errors };
    const r = ExprSchema.safeParse(input);
    if (r.success)
        return { ok: true, expr: r.data };
    return {
        ok: false,
        errors: r.error.issues.map((i) => ({
            code: "expr.invalid-operand",
            path: "/" + i.path.map(String).join("/"),
            message: i.message,
        })),
    };
}
/**
 * A zod schema that accepts exactly what parseExpr accepts, for use inside content schemas. The
 * preprocess step runs parseExpr's iterative bounds check before the recursive union sees the value,
 * so a 10,000-level input is refused instead of overflowing the stack; and unlike a pipe from
 * unknown, a preprocess emits the real Expr JSON Schema.
 */
export const ExprField = z.preprocess((v, ctx) => {
    const r = parseExpr(v);
    if (r.ok)
        return v;
    for (const e of r.errors)
        ctx.addIssue({
            code: "custom",
            message: `${e.code}${e.path ? " at " + e.path : ""}: ${e.message}`,
            path: e.path ? e.path.split("/").filter(Boolean) : [],
        });
    return z.NEVER;
}, ExprSchema);
//# sourceMappingURL=schema.js.map